import { ClassicEditor } from 'ckeditor5'
import { useWorkspace2Store } from '../../../Workspace2Store'

const FOCUS_RETRY_TIMEOUT_MS = 300

/** Live CKEditor per element, so promote/demote can focus via the editor, not
 * the DOM — a DOM focus change fires a blur, which would save. */
const editorsByElementId = new Map<string, ClassicEditor>()

const registerEditor = (
    elementId: string | undefined,
    editor: ClassicEditor
) => {
    if (!elementId) return
    editorsByElementId.set(elementId, editor)
}

// Checks identity, not just id: a remounted row's new register() can land
// before the old instance's unregister(), so an id-only delete could drop the new one.
const unregisterEditor = (
    elementId: string | undefined,
    editor: ClassicEditor
) => {
    if (elementId && editorsByElementId.get(elementId) === editor)
        editorsByElementId.delete(elementId)
}

const getRegisteredEditor = (
    elementId: string | undefined
): ClassicEditor | undefined =>
    elementId ? editorsByElementId.get(elementId) : undefined

export const activeEditorRegistry = {
    register: registerEditor,
    unregister: unregisterEditor,
    get: getRegisteredEditor,
}

/** Module state, not zustand — nothing subscribes for render. Marks the window
 * between a local re-parent and focus landing back, to suppress the resulting blur. */
let isLocalMoveInFlight = false

/** Bumped per move, so a superseded retry loop can abort. */
let focusRetryToken = 0

export const isLocalMoveActive = (): boolean => isLocalMoveInFlight

export const isLocalPromoteDemoteBlur = (event?: { name?: string }): boolean =>
    event?.name === 'blur' && isLocalMoveInFlight

const placeCaretAtEnd = (editor: ClassicEditor) => {
    editor.model?.change?.((writer) => {
        const root = editor.model.document.getRoot()
        if (root) writer.setSelection(writer.createRangeIn(root).end)
    })
}

const retryFocusRestore = (elementId: string) => {
    const token = ++focusRetryToken
    const deadline = Date.now() + FOCUS_RETRY_TIMEOUT_MS
    const clearIfStillCurrent = () => {
        if (focusRetryToken === token) isLocalMoveInFlight = false
    }

    const attempt = () => {
        if (focusRetryToken !== token) return

        const editor = getRegisteredEditor(elementId)
        try {
            if (editor?.state === 'ready') {
                placeCaretAtEnd(editor)
                editor.editing.view.focus()
                return clearIfStillCurrent()
            }
        } catch {
            return clearIfStillCurrent()
        }

        if (Date.now() < deadline) return requestAnimationFrame(attempt)
        clearIfStillCurrent()
    }

    requestAnimationFrame(attempt)
    // Backstop: rAF pauses in a backgrounded tab, so attempt() might never
    // run to release the guard — without this, blur-driven saves would stay off.
    setTimeout(clearIfStillCurrent, FOCUS_RETRY_TIMEOUT_MS)
}

/** Only way to start a local move — sets the guard and schedules its release together. */
export const beginLocalPromoteDemote = (elementId: string) => {
    isLocalMoveInFlight = true
    retryFocusRestore(elementId)
}

/** Text typed into an unsaved paragraph, kept outside React so nothing re-renders
 * per keystroke. Survives the row unmounting (scroll away, re-parent). */
interface ParagraphDraft {
    elementId: string
    /** Editor content, used to seed CKEditor when the row is rebuilt */
    html: string
    /** Processed changes, i.e. what a save would send */
    unsavedData: string
}

/** One slot is enough — only one paragraph can be unsaved at a time. */
let draft: ParagraphDraft | null = null

/** Saves only while `elementId` is the paragraph currently being added. */
const saveIfAdding = (elementId: string, html: string, unsavedData: string) => {
    if (elementId === useWorkspace2Store.getState().textContentElementToAdd?.id)
        draft = { elementId, html, unsavedData }
}

// Same freshness check as saveIfAdding, so nothing needs to explicitly clear
// the draft when the paragraph stops being the one being added.
const restore = (elementId?: string): ParagraphDraft | undefined =>
    elementId &&
    draft?.elementId === elementId &&
    elementId === useWorkspace2Store.getState().textContentElementToAdd?.id
        ? draft
        : undefined

export const unsavedParagraphDraft = { saveIfAdding, restore }

/** A saved node's local move waiting to be sent to the server. Module state, not
 * zustand — `handleOnBlur` needs to read it synchronously, no render subscription. */
let pendingMoveElementId: string | null = null

const queuePendingMove = (elementId: string) => {
    pendingMoveElementId = elementId
}

/** Read-and-clear, so a commit can't fire twice for the same move. */
const takePendingMove = (): string | null => {
    const id = pendingMoveElementId
    pendingMoveElementId = null
    return id
}

export const pendingPromoteDemoteMove = { queue: queuePendingMove, take: takePendingMove }

/** Test-only reset, mirroring `_FocusTrackerTestUtils`. */
export const _resetLocalPromoteDemote = () => {
    isLocalMoveInFlight = false
    focusRetryToken = 0
    editorsByElementId.clear()
    draft = null
    pendingMoveElementId = null
}

/** Test-only: sets the in-flight flag directly, skipping the focus-restore retry loop. */
export const _setLocalMoveInFlightForTest = (value: boolean) => {
    isLocalMoveInFlight = value
}
